import { useMutation } from '@tanstack/react-query';
import axios, { type AxiosError } from 'axios';

import { authApi } from '@/lib/auth/auth-api';

interface ErrorBody {
  message?: string | string[];
  /** 422 de publication produit : liste des blocages (ex. invariants pack P18). */
  errors?: string[];
  statusCode?: number;
}

export const extractErrorMessage = (err: unknown, fallback = 'Une erreur est survenue'): string => {
  if (axios.isAxiosError(err)) {
    const ax = err as AxiosError<ErrorBody>;
    const msg = ax.response?.data?.message;
    if (Array.isArray(msg)) return msg.join(' · ');
    if (typeof msg === 'string') return msg;
    const errors = ax.response?.data?.errors;
    if (Array.isArray(errors) && errors.length) return errors.join(' · ');
    if (ax.message) return ax.message;
  }
  if (err instanceof Error) return err.message;
  return fallback;
};

export const useLoginMutation = () =>
  useMutation({
    mutationKey: ['auth', 'login'],
    mutationFn: (params: { email: string; password: string }) =>
      authApi.login(params.email, params.password),
  });

export const useVerify2faMutation = () =>
  useMutation({
    mutationKey: ['auth', 'verify-2fa'],
    mutationFn: (params: { pendingToken: string; code: string }) =>
      authApi.verify2fa(params.pendingToken, params.code),
  });

export const useForgotPasswordMutation = () =>
  useMutation({
    mutationKey: ['auth', 'forgot-password'],
    mutationFn: (params: { email: string }) => authApi.forgotPassword(params.email),
  });

export const useResetPasswordMutation = () =>
  useMutation({
    mutationKey: ['auth', 'reset-password'],
    mutationFn: (params: { token: string; password: string }) =>
      authApi.resetPassword(params.token, params.password),
  });

export const useLogoutMutation = () =>
  useMutation({
    mutationKey: ['auth', 'logout'],
    mutationFn: (params: { refreshToken: string }) => authApi.logout(params.refreshToken),
  });
